import { TimelineInitDataProvider, SupabaseAssetInstance, SupabaseClipInstance } from '@/modules/timelineInit/types';
import { v1, type V1Result } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { assetInstanceToLegacy } from '@/infrastructure/api/v1/mappers/assetInstances';
import { clipInstanceToLegacy } from '@/infrastructure/api/v1/mappers/clipInstances';
import { uiElementInstanceToLegacy, type LegacyUiElementInstance } from '@/infrastructure/api/v1/mappers/uiElementInstances';
import { fetchSceneRow } from '@/modules/databaseService/scenes';

const UI_ELEMENT_DEFAULT_DURATION = 10;

/** A read's data, or its problem thrown: a timeline built without it would show the scene emptier than it is. */
function loaded<T>(result: V1Result<T>): T {
  if (result.error) throw result.error;
  return result.data;
}

/** The asset row a screen UI element shows on the timeline as. */
export function uiElementTimelineRow(ui: LegacyUiElementInstance) {
  const beginTime = ui.begin_time ?? 0;
  const endTime = ui.end_time ?? beginTime + UI_ELEMENT_DEFAULT_DURATION;
  return {
    asset_instance_id: ui.instance_id,
    user_id: '',
    asset_type_id: ui.ui_element_type_id,
    scene_instance_id: ui.scene_instance_id,
    asset_instance_source_name: ui.source_name || 'screen_ui',
    asset_instance_display_name: ui.display_name || 'Screen Message',
    asset_instance_img_url: '',
    asset_instance_category: 'screen_ui',
    asset_instance_position: '0,0,0',
    asset_instance_rotation: '0,0,0',
    asset_instance_scale: '1,1,1',
    asset_instance_begin_time: beginTime,
    asset_instance_end_time: endTime,
    asset_instance_is_active: true,
    asset_instance_is_locked: false,
    asset_instance_display_order: null,
  };
}

export class TimelineInitAdapter implements TimelineInitDataProvider {
  async fetchSceneAssetsAndClips(sceneId: string): Promise<{ assets: SupabaseAssetInstance[]; clips: SupabaseClipInstance[]; scenePayload?: Record<string, unknown> | null }> {
    // Any read that fails fails the timeline
    const [assetsResponse, clipsResponse, sceneResponse, uiElementsResponse] = await Promise.all([
      // Display order, then creation
      listAll(page => v1.GET('/api/v1/asset-instances', { params: { query: { sceneUniqueId: [sceneId], ...page } } })),
      listAll(page => v1.GET('/api/v1/clip-instances', { params: { query: { sceneUniqueId: [sceneId], ...page } } })),
      fetchSceneRow(sceneId),
      listAll(page => v1.GET('/api/v1/ui-element-instances', { params: { query: { sceneUniqueId: [sceneId], isActive: true, ...page } } })),
    ]);
    const assets = loaded(assetsResponse).map(assetInstanceToLegacy) as unknown as SupabaseAssetInstance[];
    const clips = loaded(clipsResponse).map(clipInstanceToLegacy) as unknown as SupabaseClipInstance[];
    const scene = loaded(sceneResponse);

    const syntheticUIAssets = loaded(uiElementsResponse).map(uiElementInstanceToLegacy).map(uiElementTimelineRow);

    return {
      assets: [...assets, ...syntheticUIAssets],
      clips,
      scenePayload: (scene.scene_instance_payload as unknown as Record<string, unknown> | null) || null
    };
  }
}
